import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { FilterDetail } from '../../../../models/api/reports/filter-detail-response';
import { FiltersService } from '../../../../services/filters/filters.service';
import { ReportsService } from '../../../../services/reports/reports.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { RadioButtonFieldComponent } from '../../../core/fields/radio-button-field.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { EditFilterComponent } from '../../edit-filter/edit-filter.component';
import { ReportModel } from '../../../../models/reports/report-model';

@Component({
  selector: 'app-filter-report-step',
  imports: [
    ActionRowComponent,
    ButtonComponent,
    RadioButtonFieldComponent,
    PageHeaderComponent,
    SelectFieldComponent,
    EditFilterComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `<div class="tw-py-8">
      <app-page-header label="Filter detail" headerSize="h2">
        <ng-template #right>
          <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
        </ng-template>
      </app-page-header>
      <div class="tw-flex tw-flex-col tw-gap-0 tw-w-full">
        <app-radio-button-field
          [control]="filterOption"
          label="Please select"
          labelClasses="tw-mb-3 tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[400px]"
          optionLabel="name"
          optionValue="code"
          [options]="filterOptions"
          placeholder=""
          [showClear]="false"
        />
      </div>
      @if (filterOption.value === 'exist') {
        <app-select-field
          [control]="filterRef"
          label="Filter"
          labelClasses="tw-mb-3 tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[400px]"
          optionLabel="name"
          optionValue="code"
          [options]="availableFilters()"
          placeholder=""
          [filter]="false"
          [showClear]="false"
          [virtualScroll]="false"
          [group]="true"
        />
      }
      @if (filterOption.value === 'copy') {
        <app-select-field
          [control]="copyFilterRef"
          label="Copy filter"
          labelClasses="tw-mb-3 tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[400px]"
          optionLabel="name"
          optionValue="code"
          [options]="availableFilters()"
          placeholder=""
          [filter]="false"
          [showClear]="false"
          [virtualScroll]="false"
          [group]="true"
          helpText="Select a filter to copy"
        />

        @if (copiedFilterDetail()) {
          <app-edit-filter
            #copyFilter
            [filterDetail]="copiedFilterDetail()"
            [isCopyMode]="true"
          />
        }
      } @else if (filterOption.value === 'new') {
        <app-edit-filter #newFilter />
      }
    </div>
    <app-action-row>
      <app-button right1 emphasis="Low" label="Previous" (click)="previous()" />
      <app-button right2 label="Next" [disabled]="invalid" (click)="next()" />
    </app-action-row>`,
  styles: [],
})
export class FilterReportComponent extends BaseComponent implements OnInit {
  @ViewChild('copyFilter')
  copyFilter: EditFilterComponent;

  @ViewChild('newFilter')
  newFilter: EditFilterComponent;

  @Input()
  stepper: StepperComponent;

  private reports = inject(ReportsService);
  private filtersService = inject(FiltersService);
  private snackbar = inject(Snackbar);

  copiedFilterDetail = signal<FilterDetail | null>(null);

  filterOption = new FormControl<string>(null, [Validators.required]);
  filterRef = new FormControl<string>(null, [Validators.required]);
  copyFilterRef = new FormControl<string>(null, [Validators.required]);

  availableFilters = signal<
    { name: string; items: { name: string; code: string }[] }[]
  >([]);

  private lastFilterOption: string | null = null;
  private lastFilterRef: string | null = null;

  readonly filterOptions = [
    { name: 'Use an existing filter', code: 'exist' },
    { name: 'Copy an existing filter', code: 'copy' },
    { name: 'Create a new filter', code: 'new' },
  ];

  get invalid(): boolean {
    if (this.filterOption.invalid) {
      return true;
    }

    if (this.filterOption.value === 'exist' && this.filterRef.invalid) {
      return true;
    }

    if (this.filterOption.value === 'copy' && this.copyFilterRef.invalid) {
      return true;
    }

    if (
      this.filterOption.value === 'copy' ||
      this.filterOption.value === 'new'
    ) {
      const editor =
        this.filterOption.value === 'copy' ? this.copyFilter : this.newFilter;
      if (!editor?.fg.controls.filterName.valid) {
        return true;
      }
      if (!editor?.hasAnyCriteria()) {
        return true;
      }
    }

    return false;
  }

  async ngOnInit(): Promise<void> {
    await this.loadAvailableFilters();
    this.loadFromReport();
    this.setupFormListeners();
  }

  private async loadAvailableFilters(refreshCache = false): Promise<void> {
    const report = this.reports.currentReport();

    await this.filtersService.loadAllFilters(
      'JURIS',
      report.reportTemplate.model as ReportModel,
      report.reportInfo.contractNumber,
      refreshCache,
    );

    const groupedFilters = [
      {
        name: 'System filters',
        items: this.filtersService.systemFilters().map((f) => ({
          name: f.filterName.replace(' (Standard Filter)', ''),
          code: f.filterId,
        })),
      },
      {
        name: 'My filters',
        items: this.filtersService.userFilters().map((f) => ({
          name: f.filterName,
          code: f.filterId,
        })),
      },
    ];

    this.availableFilters.set(groupedFilters);
  }

  private loadFromReport(): void {
    const report = this.reports.currentReport();

    if (report.reportInfo?.quickFilterId) {
      this.filterOption.setValue('exist', { emitEvent: false });
      this.filterRef.setValue(report.reportInfo.quickFilterId, {
        emitEvent: false,
      });
    }

    this.lastFilterOption = this.filterOption.value;
    this.lastFilterRef = this.filterRef.value;
  }

  private setupFormListeners(): void {
    this.dispose.subs = this.filterOption.valueChanges.subscribe((newValue) => {
      if (
        this.lastFilterOption !== null &&
        newValue !== this.lastFilterOption
      ) {
        this.showConfirmDialog('filterOption', newValue);
      } else {
        this.filterRef.markAsUntouched();
        this.filterRef.markAsPristine();
        this.filterRef.reset();
        this.copyFilterRef.markAsUntouched();
        this.copyFilterRef.markAsPristine();
        this.copyFilterRef.reset();
        this.reports.pendingFilter.set(null);
        this.saveCurrent();
      }
    });

    this.dispose.subs = this.filterRef.valueChanges.subscribe((newValue) => {
      if (this.lastFilterRef !== null && newValue !== this.lastFilterRef) {
        this.showConfirmDialog('filterRef', newValue);
      } else {
        this.saveCurrent();
      }
    });

    this.dispose.subs = this.copyFilterRef.valueChanges.subscribe(
      async (filterId) => {
        if (filterId) {
          this.copiedFilterDetail.set(null);
          this.dispose.timeouts = await new Promise((resolve) =>
            setTimeout(resolve, 0),
          );
          const report = this.reports.currentReport();
          const detail = await this.filtersService.getFilterDetail(
            filterId,
            report.reportInfo.contractNumber,
          );
          this.copiedFilterDetail.set({ ...detail });
        } else {
          this.copiedFilterDetail.set(null);
        }
        this.saveCurrent();
      },
    );
  }

  private showConfirmDialog(
    field: 'filterOption' | 'filterRef',
    newValue: string,
  ): void {
    if (field === 'filterOption') {
      this.filterOption.setValue(this.lastFilterOption, { emitEvent: false });
    } else {
      this.filterRef.setValue(this.lastFilterRef, { emitEvent: false });
    }

    this.confirm.show({
      label: 'Change filter selection',
      message: `Are you sure you want to change the filter selection? All previously defined filter criteria will be removed.`,
      width: '35rem',
      confirm: () => {
        if (field === 'filterOption') {
          this.filterOption.setValue(newValue, { emitEvent: false });
          this.lastFilterOption = newValue;
          this.filterRef.markAsUntouched();
          this.filterRef.markAsPristine();
          this.filterRef.reset();
          this.copyFilterRef.markAsUntouched();
          this.copyFilterRef.markAsPristine();
          this.copyFilterRef.reset();
          this.reports.pendingFilter.set(null);
        } else {
          this.filterRef.setValue(newValue, { emitEvent: false });
          this.lastFilterRef = newValue;
        }
        this.saveCurrent();
      },
    });
  }

  private saveCurrent(): void {
    const selectedFilterId =
      this.filterOption.value === 'exist' ? this.filterRef.value : null;

    this.reports.currentReport.update((report) => ({
      ...report,
      reportInfo: {
        ...report.reportInfo!,
        quickFilterId: selectedFilterId,
      },
    }));
  }

  previous() {
    this.saveCurrent();
    this.stepper.previous();
  }

  next() {
    if (this.filterOption.value === 'exist') {
      this.saveCurrent();
      this.stepper.next();
      return;
    }

    const editor =
      this.filterOption.value === 'copy' ? this.copyFilter : this.newFilter;
    if (!editor) return;

    if (!editor.fg.controls.filterName.valid) {
      editor.fg.controls.filterName.markAllAsTouched();
      this.snackbar.show(
        'Please enter a filter name',
        'Error',
        'fa-light fa-exclamation-triangle',
        3000,
      );
      return;
    }

    if (!editor.validCriteria()) return;

    const report = this.reports.currentReport();
    const contractNumber = report.reportInfo.contractNumber;
    const criteria = editor.getCriteria();
    const filterName = editor.fg.controls.filterName.value;

    this.reports.pendingFilter.set({ contractNumber, filterName, criteria });
    this.stepper.next();
  }
}
